<script setup lang="ts">
import { ref, computed } from 'vue'

const props = defineProps<{
  project?: number;
  projects?: number[];
}>()

// Current month and year
const today = new Date()
const selectedDate = ref<Date>(new Date())
const currentMonth = ref(today.getMonth()) // 0-indexed
const currentYear = ref(today.getFullYear())

// Get total days in a month
function getDaysInMonth(month: number, year: number): number {
  return new Date(year, month + 1, 0).getDate()
}

// Get day of week (0-6) of the first of the month
function getStartDayOfWeek(month: number, year: number): number {
  return new Date(year, month, 1).getDay()
}

function isSameDate(d1: Date, d2: Date): boolean {
    return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
    )
  }

// Build the full calendar grid (including trailing/leading days)
const calendarDays = computed(() => {
  const days: { date: Date; currentMonth: boolean }[] = []
  const year = currentYear.value
  const month = currentMonth.value

  const startDay = getStartDayOfWeek(month, year)
  const daysInMonth = getDaysInMonth(month, year)

  const prevMonth = month === 0 ? 11 : month - 1
  const prevMonthYear = month === 0 ? year - 1 : year
  const daysInPrevMonth = getDaysInMonth(prevMonth, prevMonthYear)

  // Fill leading days from previous month
  for (let i = startDay - 1; i >= 0; i--) {
    days.push({
      date: new Date(prevMonthYear, prevMonth, daysInPrevMonth - i),
      currentMonth: false,
    })
  }

  // Fill current month days
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({
      date: new Date(year, month, i),
      currentMonth: true,
    })
  }

  // Fill trailing days from next month to complete the grid
  const totalCells = Math.ceil(days.length / 7) * 7
  const nextMonth = (month + 1) % 12
  const nextMonthYear = month === 11 ? year + 1 : year
  const trailingDays = totalCells - days.length
  for (let i = 1; i <= trailingDays; i++) {
    days.push({
      date: new Date(nextMonthYear, nextMonth, i),
      currentMonth: false,
    })
  }

  return days
})
</script>

<template>
    <div class="flex justify-between mb-4 items-center">
        <div class="flex flex-row gap-2">
            <!-- Month + Year header -->
            <button @click="currentMonth = (currentMonth + 11) % 12; if (currentMonth === 11) currentYear--">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                </svg>
            </button>
            <h2 class="text-lg font-bold text-center w-36">
                {{ new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long', year: 'numeric' }) }}
            </h2>
            <button @click="currentMonth = (currentMonth + 1) % 12; if (currentMonth === 0) currentYear++">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
            </button>
        </div>
          <div class="space-x-2">
            <select>
                <option value="0">All Projects</option>
                <option v-for="project in props.projects" :key="project" :value="project">
                    Project {{ project }}
                </option>
            </select>
          </div>
        </div>
  
        <div class="grid grid-cols-7 gap-2">
          <div class="text-center font-bold">Sun</div>
          <div class="text-center font-bold">Mon</div>
          <div class="text-center font-bold">Tue</div>
          <div class="text-center font-bold">Wed</div>
          <div class="text-center font-bold">Thu</div>
          <div class="text-center font-bold">Fri</div>
          <div class="text-center font-bold">Sat</div>
  
        <div
            v-for="(day, index) in calendarDays"
            @click="selectedDate = day.date"
            :key="index"
            class="p-2 border rounded h-[100px] w-[100px] text-xs cursor-pointer transition-all duration-150"
            :class="[
                day.currentMonth ? 'bg-white' : 'bg-gray-200 text-gray-500',
                isSameDate(day.date, today) && !isSameDate(today, selectedDate) ? 'border-teal-700  text-teal-700' : 'border-gray-300',
                isSameDate(day.date, selectedDate) ? 'bg-pink-100 border-pink-500 text-pink-700' : 'hover:bg-gray-50 shadow-sm'
            ]"
        >
            {{ day.date.getDate() }}
        </div>
    </div>
</template>